import React, { useEffect, useRef } from "react";
import { useCaptcha, isCaptchaEnabled } from "./useCaptcha";

// Cloudflare Turnstile. The widget is rendered explicitly via the global `turnstile` API so this component
// fully owns the provider — swapping to another provider (hCaptcha, reCAPTCHA) touches only this file. The
// backend verifier is already provider-agnostic (POSTs secret+response to the configured VerifyUrl and reads
// the boolean `success`), so no backend/controller/PageFunction changes are needed for the switch.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Inject the Turnstile script once, shared across every instance/mount.
let scriptPromise = null;
const loadTurnstile = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Reusable CAPTCHA gate (Cloudflare Turnstile). Import it, spread the hook's `bind`, and read the result —
 * nothing else. This component hides the whole verification flow: the provider, the site key, the widget
 * lifecycle, expiry/error handling, and the `X-Captcha-Token` header (the API controller sets that from the
 * token you pass to it).
 *
 * Usage (see useCaptcha):
 *   const captcha = useCaptcha();
 *   <Captcha {...captcha.bind} />
 *   <button disabled={!captcha.isVerified}>Submit</button>
 *
 * Props (all supplied by `useCaptcha().bind`, so callers normally never pass them by hand):
 *   widgetRef  — the hook's ref; this component publishes a { reset } handle onto it
 *   onVerify   — called with the token when the challenge is solved
 *   onExpire   — called (no args) when the token expires or the widget errors
 *   className  — optional wrapper class for layout
 */
export const Captcha = ({ widgetRef, onVerify, onExpire, className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Dev-skip parity with the backend: no site key => no widget; the hook already reports isVerified === true.
    if (!isCaptchaEnabled()) return undefined;

    let widgetId;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onVerify && onVerify(token),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": (code) => {
            // Surface Turnstile's error code (e.g. 110200 = domain not allowed) for easier debugging.
            console.warn("Turnstile error", code);
            if (onExpire) onExpire();
          },
        });
        // Publish a reset handle so the hook's reset() can re-arm the widget after submit / a server rejection.
        if (widgetRef) {
          widgetRef.current = { reset: () => window.turnstile && window.turnstile.reset(widgetId) };
        }
      })
      .catch(() => {
        // Script failed to load (blocked / offline). Leave the gate unverified so the submit stays disabled.
      });

    return () => {
      cancelled = true;
      if (widgetId != null && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
      if (widgetRef) widgetRef.current = null;
    };
    // onVerify/onExpire are stable (useCallback in useCaptcha), so render the widget once.
  }, []);

  if (!isCaptchaEnabled()) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
};

export { useCaptcha, isCaptchaEnabled };
export default Captcha;
